<script setup>

import { thumbailForSpeaker } from '@/composables/helpers/speakers';

const props = defineProps({
    speaker: {
        type: Object,
        required: true
    },
    hero: {
        type: Boolean,
        required: false,
        default: false,
    }
})

const thumbnail = computed(() => {
    if (props.speaker.profile_image?.length && props.speaker.profile_image[0]?.thumbnails?.large) {
        return props.speaker.profile_image[0].thumbnails.large;
    }
    return null;
})

</script>
<template>
    <div class="speaker-header" :class=" { 'hero': hero }">
        <div class="container">
            <ProjectBreadcrumbs v-if="hero" />
            <div class="speaker">
                <MessImage  v-if="thumbnail" :image="thumbnail" lazy class="speaker-image" :alt="`${speaker.name} profile photo`" />
                <div class="speaker-image" v-else>
                    <img src="@/assets/img/speaker-default.jpg" alt="Generic person image" loading="lazy" />
                </div>
                <div class='bio'>
                    <h1 class='h1' v-if="hero" v-html="speaker.name"></h1>
                    <h2 class='h1' v-else v-html="speaker.name"></h2>
                    <p class="job-title">
                        <span v-if="speaker.job_title" v-html="speaker.job_title"></span>
                        <span v-if="speaker.company">&nbsp;@ {{ speaker.company }}</span>
                    </p>
                    <FragmentsSocial 
                        :linkedin="speaker.linkedin_page"
                        :twitter="speaker.twitter_handle ? `https://x.com/${speaker.twitter_handle}` : false"
                        :facebook="speaker.facebook_page"
                        :threads="speaker.threads_page"
                        :tiktok="speaker.tiktok_channel"
                        :website="speaker.website"
                    />
                    <p class="description" v-if="speaker.bio" v-html="speaker.bio"></p>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .speaker-header {

        color: var(--sage);

        background-color: var(--teal);
        padding-bottom: 5.1875rem;

        &.hero {
            background-image: url('@/assets/img/backgrounds/bubble-teal-7-small.png');
            background-position: right var(--header-height);
            background-repeat: no-repeat, no-repeat;

            @include hero-padding;
            padding-top: calc(var(--hero-padding-top) + 2.4375rem);

            @media (min-width: $tablet) {
                background-image: url('@/assets/img/backgrounds/bubble-1.png');
                background-position: right top;
                padding-top: calc(var(--hero-padding-top) + 2.4375rem);
                padding-bottom: 5.1875rem;
            }
        }

        .speaker {
            display: flex;
            align-items: start;
            gap: 1.75rem;
            flex-direction: column;

            @media (min-width: $tablet) {
                flex-direction: row;
                gap: 5.5625rem;
            }
        }

        .speaker-image {
            position: relative;
            border-radius: var(--radius);
            overflow: hidden;
            width: 100%;
            max-width: 100%;
            aspect-ratio: 1/1;
            flex-shrink: 0;
            max-width: 27.5rem;

            @media (min-width: $tablet) {
                width: 50%;

                &:before {
                    content: '';
                    position: absolute;
                    display: block;
                    height: 100%;
                    width: 100%;
                    pointer-events: none;
                    background-color: transparent;
                    background-image: url('@/assets/img/backgrounds/bubble-teal-22-small.png');
                    background-position: left bottom;
                    background-repeat: no-repeat, no-repeat;
                }                

            }
        }

        .h1 {
            color: var(--cream);
            font-weight: 500;
            font-size: 2.5rem;
            line-height: 3rem;
            letter-spacing: -0.05rem;
            margin-bottom: 1.75rem;            

            @media (min-width: $tablet) {
                font-size: 3.625rem;
                line-height: 4rem;
                letter-spacing: -0.10875rem;
                margin-bottom: 1.75rem;
            }
        }

        .job-title {
            color: var(--cream);
            font-size: 0.9375rem;
            font-weight: 500;
            line-height: 1.5rem; /* 160% */
            letter-spacing: 0.75px;
            text-transform: uppercase;
            margin-bottom: 2.1875rem;
        }

        .description {
            font-size: 1.0625rem;
            font-weight: 300;
            line-height: 1.75rem; /* 164.706% */
            letter-spacing: 0.02125rem;
        }
    }
</style>